import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { FieldArray } from 'formik'
import { Trash, WarningCircle, Plus } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import { getTimeOptions, maxNumberOfReminder } from '../../../common/helpers'
import NighttimeInfo from '../NighttimeInfo'

const isRemiderDateBeforeNow = (selectedTimeStart, hoursToSubtract) => {
  const now = new Date()
  const startTime = new Date(selectedTimeStart)

  startTime.setHours(startTime.getHours() - hoursToSubtract)

  return now.getTime() > startTime.getTime()
}

const ReminderArrayInputs = ({ name, values, timeStart, setFieldValue }) => {
  const { t } = useTranslation(['eventManagement'])

  const filteredOptions = useMemo(() => {
    const quietHoursStartHour = 7
    const quietHoursEndHour = 20

    return getTimeOptions(t).map((option) => {
      const [numStr, unit] = option.value.split(' ')
      const num = parseInt(numStr)

      const startDate = new Date(timeStart)
      const adjustedDate = new Date(startDate)

      let isReminderBeforeToday = true

      switch (unit.toLowerCase()) {
        case 'hour':
        case 'hours':
          isReminderBeforeToday = isRemiderDateBeforeNow(timeStart, num)
          adjustedDate.setHours(adjustedDate.getHours() - num)
          break
        case 'day':
        case 'days':
          isReminderBeforeToday = isRemiderDateBeforeNow(timeStart, num * 24)
          adjustedDate.setDate(adjustedDate.getDate() - num)
          break
        default:
          throw new Error('Unsupported unit')
      }

      const adjustedHour = adjustedDate.getHours()

      const isDuringQuietHours =
        adjustedHour <= quietHoursStartHour || adjustedHour >= quietHoursEndHour
      const isAlreadySelected = values.includes(option.value)

      const disabled = isDuringQuietHours || isAlreadySelected || isReminderBeforeToday

      return { ...option, disabled }
    })
  }, [t, timeStart, values])

  return (
    <>
      <NighttimeInfo />
      <FieldArray name={name}>
        {(arrayHelpers) => {
          return (
            <React.Fragment>
              {values.map((value, index) => {
                return (
                  <div className="columns" key={`${name}-${index}`}>
                    <div className="column is-12 event-form-reminder-input">
                      <span>{t('eventManagement:common.sendReminder')}</span>
                      <SelectField
                        options={filteredOptions}
                        name={name}
                        value={value}
                        onChange={(e) => setFieldValue(`${name}[${index}]`, e.target.value)}
                        selectStyle={{ width: '100%' }}
                        renderRightElement={(opt) =>
                          opt.disabled && <WarningCircle size={16} className="disabled-icon" />
                        }
                      />
                      {!!index && (
                        <button
                          className="button button-lightgray"
                          onClick={() => arrayHelpers.remove(index)}
                        >
                          <Trash size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}

              {values.length < maxNumberOfReminder && (
                <div className="columns">
                  <div className="column is-12">
                    <button
                      className="button button-lightgray"
                      onClick={() => arrayHelpers.push('')}
                      type="button"
                    >
                      <Plus size={20} /> {t('eventManagement:common.addNew')}
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        }}
      </FieldArray>
    </>
  )
}

ReminderArrayInputs.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.array.isRequired,
  timeStart: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
}

export default ReminderArrayInputs
